import React from 'react';
import { useTranslations } from 'use-intl';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Receipt, ReceiptStatus } from '@/types/receipt';
import SekEquivalent from '@/components/shared/SekEquivalent';
import PaymentVerificationBadge from '@/components/shared/PaymentVerificationBadge';
import { useFormatters } from '@financialharmony/ui';
import { useReceiptLabels } from './useReceiptLabels';

const STATUS_CLASSNAME: Record<ReceiptStatus, string> = {
  unmatched: 'bg-amber-50 text-amber-700 border-amber-200',
  matched: 'bg-green-50 text-green-700 border-green-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
};

interface ReceiptsTableProps {
  receipts: Receipt[];
  onMatch: (receipt: Receipt) => void;
  onRowClick: (receipt: Receipt) => void;
  formatCurrency: (amount: number, currency: string) => string;
}

const ReceiptsTable: React.FC<ReceiptsTableProps> = ({ receipts, onMatch, onRowClick, formatCurrency }) => {
  const t = useTranslations('consultantBooks.receipts');
  const labels = useReceiptLabels();
  const { formatDate: formatDateValue } = useFormatters();
  const formatDate = (dateStr: string) => formatDateValue(dateStr, 'numeric');

  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('table.columns.vendor')}</TableHead>
            <TableHead>{t('table.columns.amount')}</TableHead>
            <TableHead>{t('table.columns.currency')}</TableHead>
            <TableHead>{t('table.columns.status')}</TableHead>
            <TableHead>{t('table.columns.date')}</TableHead>
            <TableHead>{t('table.columns.matchedTransaction')}</TableHead>
            <TableHead>{t('table.columns.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {receipts.map((receipt) => (
            <TableRow
              key={receipt.id}
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => onRowClick(receipt)}
            >
              <TableCell>
                {receipt.vendor || receipt.description || '—'}
              </TableCell>
              <TableCell>
                {formatCurrency(receipt.amount, receipt.currency)}
                <SekEquivalent sekAmount={receipt.sek_amount} currency={receipt.currency} />
              </TableCell>
              <TableCell>{receipt.currency}</TableCell>
              <TableCell>
                <Badge variant="outline" className={STATUS_CLASSNAME[receipt.status]}>
                  {labels.status(receipt.status)}
                </Badge>
                <PaymentVerificationBadge paymentVerification={receipt.paymentVerification} />
              </TableCell>
              <TableCell>{receipt.due_date ? formatDate(receipt.due_date) : formatDate(receipt.created_at)}</TableCell>
              <TableCell>
                {receipt.matched_transaction_id
                  ? receipt.matched_transaction_id.slice(0, 8) + '...'
                  : '—'}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {receipt.status === 'unmatched' || receipt.status === 'rejected' ? (
                    // #498 whole-branch review finding I5 — the design spec (authoritative per
                    // this plan's ratified Q2) requires a single stable "Mark as paid" label for
                    // BOTH payables AND receipts, never changing based on connection/match state
                    // (docs/superpowers/specs/2026-08-02-merge-paid-externally-match-action-design.md,
                    // lines 171/236). The plan's own earlier "leave receipts unchanged" note (Q2's
                    // component-refactor section) was based on a narrower reading than the design
                    // spec actually calls for. Mirrors InvoiceActionMenu.tsx's payable label
                    // exactly — "Match"/"Re-match" (and 'rejected' is still re-matchable, same
                    // underlying flow) both collapse into "Mark as paid".
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); onMatch(receipt); }}
                    >
                      {t('markAsPaid')}
                    </Button>
                  ) : (
                    <span className="text-sm text-muted-foreground">
                      {receipt.matched_at
                        ? t('table.matchedOn', { date: formatDate(receipt.matched_at) })
                        : t('table.matched')}
                    </span>
                  )}
                  {/* #498 Task 8 — correction/reopen path (design spec "Correction path" UX
                      Requirement, AC14, User Flow 7). Investigation (see task-8-report.md)
                      confirmed this table previously offered no way back into the match action
                      once a receipt reached 'matched' — the only way to fix a mistaken date or
                      match was to delete and recreate the record. Reopening reuses the SAME
                      MarkAsMatchedModal (no separate "edit" mode).

                      #498 Task 8 REVIEW FIX — deliberately excludes a GENUINE bank match (the
                      review's Finding 4 scoping decision — see InvoiceActionMenu.tsx's identical
                      comment for the payable case, and ReceivablesTable.tsx's for the receivable
                      case, which has the same root cause). Receipts have no separate
                      'manually_handled' status — matchReceipt (real match) AND
                      markAsMatchedExternally/acknowledgeExternalMatch (not a real match) all set
                      status='matched', so status alone can't distinguish them here.
                      `matched_transaction_id` is the reliable signal instead: matchReceipt is the
                      ONLY writer that sets it to a real value; both no-real-match paths always set
                      it to NULL (see receiptService.ts). matchingService.ts's
                      getReceiptTransactionSuggestions (the live JIT fallback receipts DO have,
                      unlike payables) short-circuits to `return []` whenever
                      `receipt.status === "matched"` regardless of matched_transaction_id, so
                      reopening a genuine match would hit the same zero-candidates dead end as
                      payables/receivables. */}
                  {receipt.status === 'matched' && !receipt.matched_transaction_id && (
                    // #498 whole-branch review finding I5 — same "Mark as paid" label as the
                    // primary action above, mirroring InvoiceActionMenu.tsx's payable reopen case
                    // (line ~136, also "Mark as paid", not "Edit") — a single stable label per the
                    // design spec, even when reopening an already-resolved item.
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); onMatch(receipt); }}
                    >
                      {t('markAsPaid')}
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default ReceiptsTable;
